import { Controller } from "@hotwired/stimulus"
import { startIcons } from "../import_lucid"

export default class extends Controller {
  static targets = ["overlay", "input", "results"]
  static values = { url: String }

  connect() {
    // Save the initial results HTML (all chats grouped by date)
    if (this.hasResultsTarget) {
      this._defaultHtml = this.resultsTarget.innerHTML
    }

    // Cmd/Ctrl+J, not K. K belongs to the global command palette, which is on every page now
    // — including this one. Two document-level listeners on the same chord means both open.
    this._onKeydown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'j') {
        e.preventDefault()
        this.open()
      }
    }
    document.addEventListener('keydown', this._onKeydown)
  }

  disconnect() {
    clearTimeout(this._timeout)
    document.removeEventListener('keydown', this._onKeydown)
  }

  open() {
    const el = this.overlayTarget
    el.classList.remove('hidden')
    el.style.display = ''
    this._activeIndex = -1
    setTimeout(() => {
      this.inputTarget.focus()
      startIcons(el)
    }, 50)
  }

  close() {
    const el = this.overlayTarget
    el.classList.add('hidden')
    el.style.display = 'none'
    this.inputTarget.value = ''
    this._activeIndex = -1
    // Restore default content
    if (this._defaultHtml) {
      this.resultsTarget.innerHTML = this._defaultHtml
      startIcons(this.resultsTarget)
    }
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault()
      this.close()
      return
    }

    // Keyboard navigation
    const items = this.resultsTarget.querySelectorAll('a[href]')
    if (!items.length) return

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      this._activeIndex = Math.min(this._activeIndex + 1, items.length - 1)
      this._highlightItem(items)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      this._activeIndex = Math.max(this._activeIndex - 1, 0)
      this._highlightItem(items)
    } else if (event.key === 'Enter') {
      event.preventDefault()
      if (this._activeIndex >= 0 && items[this._activeIndex]) {
        items[this._activeIndex].click()
      } else if (items.length > 0) {
        items[0].click()
      }
    }
  }

  _highlightItem(items) {
    items.forEach((item, i) => {
      if (i === this._activeIndex) {
        item.classList.add('bg-purple-50', 'text-gray-900')
        item.scrollIntoView({ block: 'nearest' })
      } else {
        item.classList.remove('bg-purple-50', 'text-gray-900')
      }
    })
  }

  search() {
    clearTimeout(this._timeout)
    const q = this.inputTarget.value.trim()

    if (!q) {
      // Restore default grouped list
      if (this._defaultHtml) {
        this.resultsTarget.innerHTML = this._defaultHtml
        startIcons(this.resultsTarget)
      }
      return
    }

    this._timeout = setTimeout(() => {
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set('q', q)
      url.searchParams.set('search_only', '1')
      url.searchParams.set('format_modal', '1')

      fetch(url, { headers: { 'Accept': 'application/json' } })
        .then(r => r.json())
        .then(data => {
          this._activeIndex = -1
          if (data.length === 0) {
            this.resultsTarget.innerHTML = `
              <div class="py-12 text-center">
                <svg class="w-5 h-5 text-gray-300 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
                </svg>
                <p class="text-sm text-gray-400">No results for "${this._escapeHtml(q)}"</p>
              </div>`
            return
          }

          const html = '<div class="px-2">' + data.map(chat => `
            <a href="${chat.url}" class="flex items-center gap-3 px-3 py-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900 rounded-lg transition-colors duration-100">
              <i data-lucide="message-square" class="w-4 h-4 text-gray-300 flex-shrink-0"></i>
              <span class="text-sm truncate">${this._escapeHtml(chat.title)}</span>
            </a>
          `).join('') + '</div>'

          this.resultsTarget.innerHTML = html
          startIcons(this.resultsTarget)
        })
        .catch(() => {
          this.resultsTarget.innerHTML = `
            <div class="py-10 text-center">
              <p class="text-sm text-red-400">Search failed. Try again.</p>
            </div>`
        })
    }, 300)
  }

  _escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
